import classNames from 'classnames'
import { FormikContext, getIn, type FormikContextType } from 'formik'
import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BaseFieldProps,
  InputSize,
  LabelConstraint,
} from '@ui/components/Input/BaseField/BaseField'
import { Input } from '@ui/components/Input/Input'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { IconFCType } from '@ui/icons/types'

import { useWindowResize } from '@utils/domUtils'

import { NameDto } from '@model/NameDto'

import { makeResolveFlagIcon } from '@global/locale/localeIcons'

import { InputSearch } from '@shared/components/InputSearch'

import styles from './PopoverInput.module.scss'

export type PopoverOption<TId extends string | number = string | number> = NameDto<TId> & {
  startIcon?: IconFCType
  description?: string
  disabled?: boolean
  selectionVariant?: 'checkbox' | 'radio'
  trailText?: string
  infoText?: string
  badgeText?: string
}

interface CommonPopoverInputProps<T extends PopoverOption> {
  value?: T['id']
  onChange?: (option: T) => void
  setValueOnSelect?: boolean
  options?: T[]
  hasCheckMark?: boolean
  selectionVariant?: 'checkbox' | 'radio'
  hint?: string | React.ReactNode
  error?: boolean | string
  rightText?: BaseFieldProps['rightText']
  isValid?: boolean
  isFullWidth?: boolean
  isWidthFixed?: boolean
  disabled?: boolean
  startIcon?: IconFCType
  onClick?: () => void
  closeOnOutOfSight?: boolean
  validate?: (value: any) => string | undefined
  name?: string
  required?: boolean
  readOnly?: boolean
  maxLength?: number
  displayValue?: 'id' | 'name'
  dropdownListVariant?: 'compact' | 'default'
  size?: InputSize
  searchInput?: {
    value: string
    handleChange?: (event: ChangeEvent<HTMLInputElement>) => void
    handleClear?: () => void
    className?: string
  }
  showLabel?: boolean
  wrapText?: boolean
}

export type PopoverInputProps<T extends PopoverOption<string | number>> =
  CommonPopoverInputProps<T> & LabelConstraint

export const PopoverInput = <T extends PopoverOption<string | number>>(
  props: PopoverInputProps<T>
): JSX.Element => {
  const { t } = useTranslation()
  const {
    value,
    onChange,
    setValueOnSelect = true,
    options,
    label,
    placeholder,
    hint,
    error,
    startIcon,
    searchInput,
    isFullWidth = false,
    disabled = false,
    isWidthFixed = true,
    onClick,
    closeOnOutOfSight = false,
    validate,
    name,
    required,
    readOnly,
    maxLength,
    displayValue = 'name',
    hasCheckMark,
    dropdownListVariant = 'default',
    size = 'xl',
    showLabel,
    rightText,
  } = props

  const internalLabel = label ?? placeholder

  const internalPlaceholder = placeholder ?? label

  const inputLabelProps = internalLabel
    ? { label: internalLabel, placeholder: internalPlaceholder }
    : {}

  const isMobile = useWindowResize()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const hasOpenedDropdownRef = useRef(false)
  // Formik is optional: PopoverInput is also used as a standalone controlled dropdown
  const formikContext = useContext(FormikContext) as FormikContextType<unknown> | undefined

  useEffect(() => {
    if (isDropdownOpen) {
      hasOpenedDropdownRef.current = true
      return
    }

    inputRef.current?.blur()

    if (hasOpenedDropdownRef.current && name) {
      formikContext?.setFieldTouched(name, true)
    }
    hasOpenedDropdownRef.current = false
  }, [formikContext, isDropdownOpen, name])

  useEffect(() => {
    if (!closeOnOutOfSight || !isDropdownOpen || !inputRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setIsDropdownOpen(false)
        }
      },
      {
        rootMargin: '24px',
        threshold: 0.1,
      }
    )

    observer.observe(inputRef.current)

    return () => observer.disconnect()
  }, [closeOnOutOfSight, isDropdownOpen])

  const selectedName = useMemo(() => {
    if (!options || options.length === 0) {
      return value
    }
    const option = options.find((option) => option.id === value)
    return displayValue === 'id' ? option?.id : option?.name
  }, [value, options, displayValue])

  const isFormikField = !!name && !!formikContext
  const formikError = isFormikField ? getIn(formikContext.errors, name) : undefined
  const formikTouched = isFormikField ? !!getIn(formikContext.touched, name) : true

  const validationError = validate ? validate(value) : undefined
  const fieldError =
    formikError || validationError || (typeof error === 'string' ? error : undefined)
  const hasError = !!fieldError || error === true
  const showHint = formikTouched && fieldError ? fieldError : hint

  const handleSelect = (option: T) => {
    if (option.disabled) return

    if (name && formikContext) {
      if (setValueOnSelect) formikContext.setFieldValue(name, option.id)
      formikContext.setFieldTouched(name, true)
    }
    onChange?.(option)
    setIsDropdownOpen(false)
  }

  const handleWrapperClick = () => {
    if (disabled || readOnly) return

    if (onClick) {
      onClick()
    } else {
      setIsDropdownOpen(true)
    }
  }

  const handleEndIconClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (disabled || readOnly) return

    if (onClick) {
      onClick()
      return
    }

    setIsDropdownOpen((prev) => !prev)

    if (!isDropdownOpen) {
      inputRef.current?.focus()
    } else {
      inputRef.current?.blur()
    }
  }

  const handleBlur = () => {
    if (isDropdownOpen) return
    if (name) formikContext?.setFieldTouched(name, true)
  }

  const renderOptions = (opts: typeof options) =>
    opts?.map((option) => {
      const isActive = option.id === value

      const selectionVariant =
        option.selectionVariant ?? props.selectionVariant ?? (hasCheckMark ? 'checkbox' : undefined)

      const StartIcon: IconFCType | undefined =
        option.startIcon ??
        (typeof option.id === 'string' ? makeResolveFlagIcon(option.id) : undefined)

      return (
        <ListItem
          key={option.id}
          onClick={() => handleSelect(option)}
          active={isActive}
          disabled={option.disabled}
          startSlot={StartIcon ? { icon: StartIcon } : undefined}
          endSlot={
            selectionVariant === 'checkbox'
              ? { checkbox: true }
              : selectionVariant === 'radio'
              ? { radio: true }
              : undefined
          }
          subtitle={option.description}
          titleSlot={option.badgeText ? { badgeText: option.badgeText } : undefined}
          description={{
            title: option.trailText,
            subtitle: option.infoText,
          }}
        >
          {option.name}
        </ListItem>
      )
    })

  return (
    <div
      className={classNames({
        [styles.fullWidth]: isFullWidth,
      })}
    >
      <Popover
        direction='top'
        isOpen={isDropdownOpen}
        setIsOpen={setIsDropdownOpen}
        label={label ?? placeholder}
        disabled={!!onClick || disabled}
        isWidthFixed={isWidthFixed}
        modalCanSearch={!!searchInput}
        modalRender={
          searchInput && isMobile
            ? (ctx) => (
                <PopoverCard>
                  <PopoverList variant={dropdownListVariant}>
                    {!ctx.search
                      ? renderOptions(options)
                      : renderOptions(
                          options?.filter((option) =>
                            option.name.toLowerCase().includes(ctx.search.toLowerCase())
                          )
                        )}
                  </PopoverList>
                </PopoverCard>
              )
            : undefined
        }
        content={
          <PopoverCard>
            {searchInput && !isMobile && (
              <InputSearch
                label={t('Search')}
                className={classNames(styles.searchField, searchInput?.className)}
                value={searchInput?.value}
                onChange={searchInput?.handleChange}
                onClear={searchInput?.handleClear}
                isAutoFocus
              />
            )}
            <PopoverList variant={dropdownListVariant} onMouseOver={(e) => e.stopPropagation()}>
              {!searchInput?.value
                ? renderOptions(options)
                : renderOptions(
                    options?.filter((option) =>
                      option.name.toLowerCase().includes(searchInput.value.toLowerCase())
                    )
                  )}
            </PopoverList>
          </PopoverCard>
        }
      >
        <Input
          {...inputLabelProps}
          value={selectedName || ''}
          onWrapperClick={handleWrapperClick}
          onBlur={handleBlur}
          ref={inputRef}
          rightText={rightText}
          onEndIconClick={handleEndIconClick}
          rightIcon={CaretIcon.Down}
          hint={showHint}
          error={formikTouched && hasError}
          readOnly={true}
          focused={isDropdownOpen}
          leftIcon={startIcon}
          disabled={disabled}
          name={name}
          required={required}
          maxLength={maxLength}
          size={size}
          showLabel={showLabel}
        />
      </Popover>
    </div>
  )
}
